import * as _ from 'underscore';
import { ServiceLocator } from '../../app/ServiceLocator';
import { toNativePromise } from '../../commonInfra/utils/QUtils';
import { WebClientListResourceFetcher } from '../../commonInfra/utils/resourceFetchers/WebClientListResourceFetcher';
import { ListResult } from '../../ts/fetchers/ListResult';
import { PermissionsHelper } from '../../ts/helpers/PermissionsHelper';
import { PermissionsMerger } from '../../ts/helpers/PermissionsMerger';
import { ResultHelper } from '../../httpClient/utils/ResultHelper';
import { IGranteePermissions } from '../../ts/models/IGranteePermissions';
import { IGetEffectivePermissionsResult } from '../../ts/ServerApi/requests/GetEffectivePermissionsRequest';
import { GetSiteUsersRequest, IGetSiteUsersParams, IGetSiteUsersResult } from '../../ts/ServerApi/requests/GetSiteUsersRequest';
import { GetUsersRequest, IGetUsersResult } from '../../ts/ServerApi/requests/GetUsersRequest';
import { IGetItemsParams } from '../../ts/ServerApi/types/IGetItemsParams';
import { ISiteUser } from '../../ts/ServerApi/types/ISiteUser';
import { ITypedIds } from '../../ts/ServerApi/types/ITypedIds';
import { IUser, UserKeys } from '../../ts/ServerApi/types/IUser';
import WebHttpClient from '../../httpClient/WebHttpClient';

class EffectivePermissionsService {

  private usersFetcher = new WebClientListResourceFetcher(GetUsersRequest, (result: IGetUsersResult) => result.users);
  private siteUsersFetcher = new WebClientListResourceFetcher(GetSiteUsersRequest, (result: IGetSiteUsersResult) => result.users);

  public getEffectivePermissions(
    usersParams: IGetItemsParams,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    return toNativePromise(
      this.usersFetcher.fetchList(usersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<IUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      })
    );
  }

  /**
   * Must be requested by a Site or Server admin
   */
  public getEffectivePermissionsWithSiteRole(
    siteUsersParams: IGetSiteUsersParams,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    return toNativePromise(
      this.siteUsersFetcher.fetchList(siteUsersParams)
      .then<ListResult<IGranteePermissions>>((users: ListResult<ISiteUser>) => {
        return this.getEffectivePermissionsInternal(users, authorizables);
      })
    );
  }

  private getEffectivePermissionsInternal(
    users: ListResult<IUser> | ListResult<ISiteUser>,
    authorizables: ITypedIds
  ): Promise<ListResult<IGranteePermissions>> {
    if (users.items.length === 0) {
      // no users; return empty permission list
      return new Promise((resolve) => {
        resolve(new ListResult<IGranteePermissions>([]));
      });
    } else {
      const userIds = _.pluck(users.items, UserKeys.id);
      const requests = PermissionsHelper.effectivePermissionsRequestsForAuthorizables(userIds, authorizables);
      const combineResultsFn = (results: IGetEffectivePermissionsResult[]) => {
        return { permissions: ResultHelper.combine(results, (result) => result.permissions) };
      };

      return WebHttpClient.sendRequestsParallel(requests, combineResultsFn)
        .then<ListResult<IGranteePermissions>>((result: IGetEffectivePermissionsResult) => {
          const list = PermissionsMerger.makeGranteePermissionsList(
            result.permissions, authorizables.ids, [], users.items
          );
          return new ListResult<IGranteePermissions>(list, users.index, users.total);
        });
    }
  }
}

export const EffectivePermissions = new EffectivePermissionsService();
